/* eslint-disable arrow-body-style */
import { useEffect, useMemo, useRef } from 'react';
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Iconmoon from '@deprecated-ui/common/Iconmoon';
import color from '@deprecated-ui/_style/color';
import shallow from 'zustand/shallow';
import numeral from 'numeral';
import Image from 'global/Image';
import If from 'global/ErrorHandlers/If';
import { ORDER_TYPE } from 'constants/trading';
import { capitalizeFirstLetter, getColorBasedOnNumber } from 'utils/general';
import { clevertapChangeStock } from 'lib/analytics/securities';
import { formatNumber } from 'utils/number';
import tradingDataStore from 'lib/stores/tradingData';
import useOrderbookCompanies from 'global/Securities/stores/useOrderbookCompanies';
import { Col, Row } from 'antd';
import { TransactionFormType } from 'typings/transactionForm';
import { TransactionType } from 'global/Securities/types/transaction';
import SearchCompany from './SearchCompany';
import BidOfferBox from './BidOfferBox';
import { FormHeader } from '../styled';
import { SymbolAvatar, PriceWrapper } from './styled';
import TradingCompanyTags from './TradingCompanyTags';
import { DATA_CY_MODAL_TRADING } from '../constant';
import FormTypeSelect from '../RealTradingModal/TransactionModal/FormTypeSelect';
import TransactionTypeSwitch from './TransactionTypeSwitch';
import tradingModalStore from '../store';

interface TransactionHeaderProps {
  orderType: string;
  defaultTabsValue?: TransactionType;
  isSearchActive?: boolean;
  disableTabs?: boolean;
  showTabs?: boolean;
  formTypeSelectVisible?: boolean;
  setIsSearchActive: () => void;
  onUpdateSymbol: (symbol?: string, isBond?: boolean) => void;
  onFormTypeChange?(formType: TransactionFormType): void;
  onTabsChange?(transactionType: TransactionType): void;
  borderless?: boolean;
}

function TransactionHeader({
  orderType,
  defaultTabsValue = TransactionType.Buy,
  isSearchActive,
  disableTabs = false,
  showTabs = false,
  formTypeSelectVisible = false,
  setIsSearchActive = () => {},
  onUpdateSymbol = () => {},
  onFormTypeChange = () => {},
  onTabsChange = () => {},
  borderless = false,
}: TransactionHeaderProps) {
  const stockInputRef = useRef<HTMLInputElement>();
  const { data } = tradingDataStore((s) => s.company);
  const trailingStop = tradingModalStore((s) => s.trailingStop);
  const [priceOrderbook, changeOrderbook, percentageOrderbook] =
    useOrderbookCompanies(
      (s) => [s.data.lastprice, s.data.change, s.data.percentage_change],
      shallow,
    );

  const {
    icon_url,
    name,
    symbol,
    orderbook,
    price: priceCompany,
    change: changeCompany,
    percentage: percentageCompany,
  } = data || {};

  const price = priceOrderbook || priceCompany;
  const change = changeOrderbook || changeCompany;
  const percentage = percentageOrderbook || percentageCompany;

  const isBuy = orderType === ORDER_TYPE.BUY;

  useEffect(() => {
    if (isSearchActive) {
      setTimeout(() => {
        stockInputRef.current?.focus();
      }, 100);
    }
  }, [isSearchActive]);

  const transactionTitle = useMemo(() => {
    if (trailingStop) return 'Amend Trailing Stop';

    return `${orderType
      .split('_')
      .map((s) => capitalizeFirstLetter(s))
      .join(' ')}${orderType === 'EXERCISE' ? ` ${symbol}` : ''}`;
  }, [trailingStop, orderType, symbol]);

  return (
    <>
      <Row
        style={{
          position: 'relative',
          width: '100%',
          height: 32,
          paddingRight: 24,
          marginBottom: 20,
          gap: 8,
        }}
      >
        <Flex
          align="center"
          justify="center"
          width="100%"
          height="32px"
          style={{
            position: 'absolute',
          }}
        >
          <If condition={!showTabs}>
            <Text
              family="bold"
              size="20px"
              align="center"
              // @ts-expect-error
              m="0"
              data-cy={DATA_CY_MODAL_TRADING.HEADER_TITLE}
            >
              {transactionTitle}
            </Text>
          </If>
        </Flex>

        <If condition={formTypeSelectVisible}>
          <Col span={7}>
            <FormTypeSelect onChange={onFormTypeChange} />
          </Col>
        </If>
        {showTabs && (
          <Col span={formTypeSelectVisible ? 16 : 24}>
            <TransactionTypeSwitch
              defaultValue={defaultTabsValue}
              disabled={disableTabs}
              onChange={onTabsChange}
            />
          </Col>
        )}
      </Row>

      <FormHeader
        style={{ paddingTop: showTabs ? 15 : 0 }}
        className={!borderless ? 'has-border' : ''}
      >
        <Flex
          justify="space-between"
          width="100%"
          style={{ display: isSearchActive ? 'none' : 'flex' }}
        >
          <Flex align="flex-start">
            {!icon_url ? (
              <SymbolAvatar>
                <Text color="white" family="bold" size="16px">
                  {symbol[0]}
                </Text>
              </SymbolAvatar>
            ) : (
              <Image
                src={icon_url}
                style={{ width: '40px', height: '40px', background: 'white' }}
              />
            )}
            {/* @ts-expect-error */}
            <Flex direction="column" pl="15px">
              <Flex align="center">
                <Text family="bold" size="16px">
                  {symbol}
                </Text>
                <TradingCompanyTags company={data} />
              </Flex>
              {/* @ts-expect-error */}
              <Flex mt="3px">
                {/* @ts-expect-error */}
                <Text size="14px" color={color.midgray} mr="5px">
                  {name}
                </Text>
                {isBuy && (
                  <Iconmoon
                    className="icon-tradesmall_pencil2"
                    size="14px"
                    hoverColor={color.highgray}
                    onClick={() => {
                      setIsSearchActive();
                    }}
                    data-cy={
                      DATA_CY_MODAL_TRADING.HEADER_CHANGE_EMITEN_BUTTON_PENCIL
                    }
                  />
                )}
              </Flex>
            </Flex>
          </Flex>
          <Flex align="center">
            <PriceWrapper>
              <Text family="bold" as="div">
                {formatNumber(price, false)}
              </Text>
              <div>
                <Text
                  color={getColorBasedOnNumber(Number(change))}
                  as="span"
                  size="12px"
                >
                  {Number(change)} ({percentage > 0 && ' +'}
                  {numeral(percentage).format('0.00')}
                  %)
                </Text>
              </div>
            </PriceWrapper>
            <BidOfferBox orderbook={orderbook} />
          </Flex>
        </Flex>

        {isSearchActive && (
          <Flex width="100%">
            <SearchCompany
              ref={stockInputRef}
              isInTradingModal
              updateSymbol={(newSymbol, isBond) => {
                clevertapChangeStock();
                onUpdateSymbol(newSymbol, isBond);
              }}
            />
          </Flex>
        )}
      </FormHeader>
    </>
  );
}

export default TransactionHeader;
